import { useEffect, useRef, type PointerEvent } from 'react'

import styles from './SignaturePad.module.css'

type SignaturePadProps = {
  onChange: (dataUrl: string) => void
  /** Existing signature PNG data URL when editing an application. */
  initialValue?: string | null
}

/**
 * Canvas signature capture that emits a PNG data URL on stroke end.
 */
export function SignaturePad({ onChange, initialValue = null }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const drawing = useRef(false)
  const onChangeRef = useRef(onChange)
  const seededRef = useRef<string | null>(null)
  onChangeRef.current = onChange

  // Keep dependency array length fixed (always one entry) for create + edit mounts.
  const seed = initialValue || ''

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const ratio = window.devicePixelRatio || 1
    const width = canvas.clientWidth
    const height = canvas.clientHeight
    canvas.width = Math.floor(width * ratio)
    canvas.height = Math.floor(height * ratio)
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.scale(ratio, ratio)
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.strokeStyle = '#0b1f17'
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, width, height)

    if (!seed) {
      seededRef.current = null
      return
    }
    if (seededRef.current === seed) return

    const image = new Image()
    image.onload = () => {
      ctx.drawImage(image, 0, 0, width, height)
      seededRef.current = seed
      onChangeRef.current(seed)
    }
    image.src = seed
  }, [seed])

  function position(event: PointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current
    if (!canvas) return { x: 0, y: 0 }
    const rect = canvas.getBoundingClientRect()
    return { x: event.clientX - rect.left, y: event.clientY - rect.top }
  }

  function emit() {
    const canvas = canvasRef.current
    if (!canvas) return
    onChangeRef.current(canvas.toDataURL('image/png'))
  }

  function onPointerDown(event: PointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return
    drawing.current = true
    canvas.setPointerCapture(event.pointerId)
    const { x, y } = position(event)
    ctx.beginPath()
    ctx.moveTo(x, y)
  }

  function onPointerMove(event: PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return
    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx) return
    const { x, y } = position(event)
    ctx.lineTo(x, y)
    ctx.stroke()
  }

  function onPointerUp() {
    if (!drawing.current) return
    drawing.current = false
    emit()
  }

  function clear() {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.clientWidth, canvas.clientHeight)
    seededRef.current = null
    onChangeRef.current('')
  }

  return (
    <div className={styles.wrap}>
      <canvas
        ref={canvasRef}
        className={styles.canvas}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
      />
      <button type="button" className={styles.clear} onClick={clear}>
        Clear signature
      </button>
    </div>
  )
}
